import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import config from '../config';
import { MathJaxContext, MathJax } from 'better-react-mathjax';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { getOrganizationHeaders } from '../orgContext';
import {
  AdminDataGrid,
  AdminFilterBar,
  AdminPageHeader,
  DataGridColumn,
  DataGridRowAction,
  FilterConfig,
  HeaderAction,
  SortDirection,
} from './components/AdminMasterDetail';
import {
  AdminBreadcrumbs,
  AdminSectionHeading,
  AdminStatCard,
} from './components/AdminUiPrimitives';
import {
  IconBook,
  IconCheckCircle,
  IconDelete,
  IconEdit,
  IconGeneral,
  IconMetadata,
  IconRelationships,
} from './components/AdminIcons';

interface Course {
  id: number;
  name: string;
}

interface CourseLeafOption {
  id: number;
  name: string;
  pathLabel?: string;
}

interface CategoryLeafOption {
  id: number;
  name: string;
  label?: string;
  pathLabel?: string;
  parentCategoryId?: number | null;
  releaseVersion?: string | null;
  sortOrder?: number;
}

interface Exam {
  id: number;
  type: string;
  name: string;
  description: string | null;
  timeLimitMinutes: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  courses: Course[];
}

type SortKey = 'name' | 'type' | 'status' | 'timeLimit' | 'courses';

interface ExamRow {
  id: number;
  name: string;
  type: string;
  status: string;
  timeLimit: number;
  courses: number;
  exam: Exam;
}

interface ExamFormData {
  type: string;
  name: string;
  description: string;
  timeLimitMinutes: number;
  isActive: boolean;
  courseIds: number[];
}

interface QuestionBankItem {
  id: number;
  categoryId: number;
  questionText: string;
  difficultyLevel: number;
  isActive: boolean;
  optionA?: string | null;
  optionB?: string | null;
  optionC?: string | null;
  optionD?: string | null;
  optionE?: string | null;
  correctOption?: string | null;
}

interface ExamQuestionItem {
  id: number;
  examId: number;
  questionId: number;
  questionText: string;
  categoryId: number;
  difficultyLevel: number;
  isEnabled: boolean;
  orderIndex: number | null;
  marks: number;
  negativeMarks: number;
}

interface CategoryHierarchy {
  id: number;
  name: string;
  releaseVersion?: string | null;
  subCategories: CategoryHierarchy[];
}

interface CategoryLookupItem {
  id: number;
  name: string;
  parentCategoryId?: number | null;
  releaseVersion?: string | null;
  sortOrder?: number;
}

const parseReleaseVersion = (releaseVersion?: string | null): number | null => {
  if (!releaseVersion) return null;

  const normalized = releaseVersion.toLowerCase().trim();
  const numericMatch = normalized.match(/\d+/);
  if (!numericMatch) return null;

  const parsed = Number(numericMatch[0]);
  if (parsed >= 1 && parsed <= 4) {
    return parsed;
  }

  return null;
};

const collectCategoryMapFromLookup = (
  categories: CategoryLookupItem[],
  nameMap: Record<number, string>,
  releaseMap: Record<number, number | null>
) => {
  categories.forEach((category) => {
    nameMap[category.id] = category.name;
    releaseMap[category.id] = parseReleaseVersion(category.releaseVersion);
  });
};

const collectCategoryIds = (categories: CategoryHierarchy[]): number[] => {
  const ids: number[] = [];
  const traverse = (nodes: CategoryHierarchy[]) => {
    nodes.forEach((node) => {
      ids.push(node.id);
      if (node.subCategories?.length) {
        traverse(node.subCategories);
      }
    });
  };

  traverse(categories);
  return ids;
};

const normalizeExam = (exam: Partial<Exam> | null | undefined): Exam => {
  const examCourses = exam?.courses;

  return {
    id: exam?.id ?? 0,
    type: exam?.type || 'Exam',
    name: exam?.name || '',
    description: exam?.description ?? null,
    timeLimitMinutes: exam?.timeLimitMinutes ?? 0,
    isActive: exam?.isActive ?? true,
    createdAt: exam?.createdAt || '',
    updatedAt: exam?.updatedAt || '',
    courses: Array.isArray(examCourses) ? examCourses : [],
  };
};

const ManageExams: React.FC = () => {
  const { getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { examId } = useParams<{ examId?: string }>();
  const parsedExamId = examId ? Number(examId) : Number.NaN;
  const routeExamId = Number.isFinite(parsedExamId) && parsedExamId > 0 ? parsedExamId : null;
  const isQuestionBuilderRoute = routeExamId !== null;
  const returnToPath = searchParams.get('returnTo') || null;
  const [exams, setExams] = useState<Exam[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [courseOptions, setCourseOptions] = useState<CourseLeafOption[]>([]);
  const [courseCategoryOptions, setCourseCategoryOptions] = useState<CategoryLeafOption[]>([]);
  const [selectedCourseId, setSelectedCourseId] = useState<number | null>(null);
  const [selectedExamId, setSelectedExamId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [selectedExamForQuestions, setSelectedExamForQuestions] = useState<Exam | null>(null);
  const [questionBank, setQuestionBank] = useState<QuestionBankItem[]>([]);
  const [examQuestions, setExamQuestions] = useState<ExamQuestionItem[]>([]);
  const [categoryNameMap, setCategoryNameMap] = useState<Record<number, string>>({});
  const [categoryReleaseVersionMap, setCategoryReleaseVersionMap] = useState<Record<number, number | null>>({});
  const [courseCategoryIds, setCourseCategoryIds] = useState<Record<number, number[]>>({});
  const [loadingQuestionBank, setLoadingQuestionBank] = useState(false);
  const [loadingLeafCategories, setLoadingLeafCategories] = useState(false);
  const [loadingBuilder, setLoadingBuilder] = useState(false);
  const [savingBuilder, setSavingBuilder] = useState(false);
  const [builderMessage, setBuilderMessage] = useState<string | null>(null);
  const [draggingQuestionId, setDraggingQuestionId] = useState<number | null>(null);
  const [searchText, setSearchText] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<number | null>(null);
  const [difficultyFilter, setDifficultyFilter] = useState<number | null>(null);
  const [courseFilter, setCourseFilter] = useState<number | null>(null);
  const [releaseVersionFilter, setReleaseVersionFilter] = useState<number | null>(null);
  const [examSearch, setExamSearch] = useState('');
  const [examStatusFilter, setExamStatusFilter] = useState('all');
  const [examTypeFilter, setExamTypeFilter] = useState('all');
  const [examSortKey, setExamSortKey] = useState<SortKey>('name');
  const [examSortDirection, setExamSortDirection] = useState<SortDirection>('asc');
  const [examPage, setExamPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [questionBankPage, setQuestionBankPage] = useState(1);

  const [formData, setFormData] = useState<ExamFormData>({
    type: 'Exam',
    name: '',
    description: '',
    timeLimitMinutes: 60,
    isActive: true,
    courseIds: []
  });

  const API_BASE_URL = `${config.apiBaseUrl}/api`;
  const QUESTION_BANK_PAGE_SIZE = 30;
  const EXAMS_PAGE_SIZE = 10;
  const BUILDER_SECTION_MIN_HEIGHT = 620;

  const getAuthToken = useCallback(async (): Promise<string> => {
    const tokenResponse = await getAccessTokenSilently().catch(() =>
      getAccessTokenSilently({ cacheMode: 'off' } as any)
    );

    const token =
      typeof tokenResponse === 'string'
        ? tokenResponse
        : (tokenResponse as { access_token?: string })?.access_token;

    if (!token) {
      throw new Error('Unable to get access token. Please sign in again and retry.');
    }

    return token;
  }, [getAccessTokenSilently]);

  const hasMathSyntax = (value?: string | null): boolean => {
    const text = (value || '').trim();
    if (!text) return false;

    return /\$|\\\(|\\\[|\\[a-zA-Z]+|\^|_/.test(text);
  };

  const prepareForMathJax = (value?: string | null): string => {
    const text = (value || '').trim();
    if (!text) return '';

    const hasDelimiters = /\$|\\\(|\\\[/.test(text);
    const looksLikeLatex = /\\[a-zA-Z]+|\^|_/.test(text);

    if (!hasDelimiters && looksLikeLatex) {
      return `\\(${text}\\)`;
    }

    return text;
  };

  const toHtmlMarkup = (value?: string | null): string => {
    const prepared = prepareForMathJax(value);
    if (!prepared) return '';

    const hasHtmlTags = /<\/?[a-z][\s\S]*>/i.test(prepared);
    if (hasHtmlTags) {
      return prepared;
    }

    const escaped = prepared
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

    return escaped.replace(/\r?\n/g, '<br/>');
  };

  const fetchExamsForCourse = useCallback(async (courseId: number | null) => {
    const token = await getAuthToken();
    try {
      const url = courseId
        ? `${API_BASE_URL}/exams/admin?courseId=${courseId}`
        : `${API_BASE_URL}/exams/admin`;
      const examsResponse = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}`, ...getOrganizationHeaders() }
      });

      const normalizedExams = (examsResponse.data || []).map((exam: Partial<Exam> | null | undefined) => normalizeExam(exam));
      setExams(normalizedExams);
    } catch (err: any) {
      if (err?.response?.status === 400) {
        setExams([]);
        return;
      }

      throw err;
    }
  }, [API_BASE_URL, getAuthToken]);

  useEffect(() => {
    if (isQuestionBuilderRoute) {
      setCourses([]);
      setLoading(false);
      return;
    }

    const loadCourses = async () => {
      try {
        setLoading(true);
        const token = await getAuthToken();
        const coursesResponse = await axios.get(`${API_BASE_URL}/courses`, {
          headers: { Authorization: `Bearer ${token}`, ...getOrganizationHeaders() }
        });
        const availableCourses: Course[] = coursesResponse.data || [];
        setCourses(availableCourses);

        if (availableCourses.length === 0) {
          setSelectedCourseId(null);
          setExams([]);
          return;
        }

        setSelectedCourseId((prev) => {
          if (prev && availableCourses.some((course) => course.id === prev)) {
            return prev;
          }
          // Default to null = "All Courses" so homework (no course) shows immediately
          return null;
        });
      } catch (err) {
        console.error('Failed to load courses for exams page:', err);
        setError('Failed to load courses');
      } finally {
        setLoading(false);
      }
    };

    loadCourses();
  }, [API_BASE_URL, getAuthToken, isQuestionBuilderRoute]);

  useEffect(() => {
    if (isQuestionBuilderRoute) {
      return;
    }

    const loadExams = async () => {
      try {
        setLoading(true);
        // Pass null to fetch ALL org exams (including homework without courses)
        await fetchExamsForCourse(selectedCourseId);
      } catch (err: any) {
        console.error('Failed to load exams:', err);
        setError(err?.response?.data?.message || err?.message || 'Failed to load exams');
      } finally {
        setLoading(false);
      }
    };

    loadExams();
  }, [fetchExamsForCourse, isQuestionBuilderRoute, selectedCourseId]);

  const loadLeafCategoriesForCourse = useCallback(async (courseId: number | null) => {
    if (!courseId) {
      setCourseCategoryOptions([]);
      setCategoryFilter(null);
      return;
    }

    try {
      setLoadingLeafCategories(true);
      setCategoryFilter(null);
      setQuestionBank([]);

      const token = await getAuthToken();
      const response = await axios.get(`${API_BASE_URL}/categories/lookup/leaf?courseId=${courseId}`, {
        headers: { Authorization: `Bearer ${token}`, ...getOrganizationHeaders() }
      });

      const nextCategories = Array.isArray(response.data) ? response.data : [];
      setCourseCategoryOptions(nextCategories);

      setCategoryFilter((previousCategoryId) => {
        if (previousCategoryId && nextCategories.some((category) => category.id === previousCategoryId)) {
          return previousCategoryId;
        }

        return nextCategories[0]?.id ?? null;
      });
    } catch (err) {
      console.error('Failed to load leaf categories for selected course:', err);
      setCourseCategoryOptions([]);
      setCategoryFilter(null);
    } finally {
      setLoadingLeafCategories(false);
    }
  }, [API_BASE_URL, getAuthToken]);

  const openQuestionBuilder = useCallback(async (exam: Exam) => {
    try {
      setBuilderMessage(null);
      setError(null);
      setSelectedExamForQuestions(exam);
      setLoadingBuilder(true);

      const token = await getAuthToken();

      const authHeaders = { Authorization: `Bearer ${token}`, ...getOrganizationHeaders() };

      const [examQuestionsResponse, categoriesResponse, courseOptionsResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/exams/${exam.id}/questions`, { headers: authHeaders }),
        axios.get(`${API_BASE_URL}/categories/lookup`, { headers: authHeaders }),
        axios.get(`${API_BASE_URL}/courses/leaf-options`, { headers: authHeaders })
      ]);

      const categoryMap: Record<number, string> = {};
      const releaseMap: Record<number, number | null> = {};
      collectCategoryMapFromLookup(categoriesResponse.data || [], categoryMap, releaseMap);
      setCategoryNameMap(categoryMap);
      setCategoryReleaseVersionMap(releaseMap);

      const allowedCourses = Array.isArray(courseOptionsResponse.data) ? courseOptionsResponse.data : [];
      setCourseOptions(allowedCourses);

      const preferredCourse =
        (exam.courses || []).find((course) => allowedCourses.some((option) => option.id === course.id)) ??
        allowedCourses[0] ??
        null;

      setSelectedCourseId(preferredCourse?.id ?? null);

      const mappings: ExamQuestionItem[] = (examQuestionsResponse.data || []).map((item: ExamQuestionItem, index: number) => ({
        ...item,
        orderIndex: item.orderIndex ?? index,
        marks: item.marks > 0 ? item.marks : 1,
        negativeMarks: item.negativeMarks >= 0 ? item.negativeMarks : 0
      }));

      setExamQuestions(mappings);
      setQuestionBank([]);
      setSearchText('');
      setDifficultyFilter(null);
      setReleaseVersionFilter(null);
      setQuestionBankPage(1);

      if (preferredCourse) {
        await loadLeafCategoriesForCourse(preferredCourse.id);
      } else {
        setCourseCategoryOptions([]);
        setCategoryFilter(null);
      }
    } catch (err) {
      console.error('Failed to load exam question builder data:', err);
      setError('Failed to load question bank or exam question mappings');
    } finally {
      setLoadingBuilder(false);
    }
  }, [API_BASE_URL, getAuthToken, loadLeafCategoriesForCourse]);

  const loadQuestionBankForCategory = useCallback(async (categoryId: number) => {
    try {
      setLoadingQuestionBank(true);

      const token = await getAuthToken();

      const response = await axios.get(`${API_BASE_URL}/questions?categoryId=${categoryId}&isActive=true`, {
        headers: { Authorization: `Bearer ${token}`, ...getOrganizationHeaders() }
      });

      setQuestionBank(response.data || []);
      setQuestionBankPage(1);
    } catch (err) {
      console.error('Failed to load question bank for category:', err);
      setQuestionBank([]);
      setError('Failed to load questions for the selected category');
    } finally {
      setLoadingQuestionBank(false);
    }
  }, [API_BASE_URL, getAuthToken]);

  useEffect(() => {
    if (!isQuestionBuilderRoute || !selectedExamForQuestions) {
      return;
    }

    if (!selectedCourseId) {
      setCourseCategoryOptions([]);
      setCategoryFilter(null);
      setQuestionBank([]);
      return;
    }

    void loadLeafCategoriesForCourse(selectedCourseId);
  }, [isQuestionBuilderRoute, loadLeafCategoriesForCourse, selectedCourseId, selectedExamForQuestions]);

  useEffect(() => {
    if (!isQuestionBuilderRoute || !selectedExamForQuestions) {
      return;
    }

    if (!categoryFilter) {
      setQuestionBank([]);
      return;
    }

    loadQuestionBankForCategory(categoryFilter);
  }, [categoryFilter, isQuestionBuilderRoute, loadQuestionBankForCategory, selectedExamForQuestions]);

  useEffect(() => {
    if (!routeExamId) {
      return;
    }

    if (selectedExamForQuestions?.id === routeExamId) {
      return;
    }

    let isCancelled = false;

    const loadBuilderExam = async () => {
      try {
        const token = await getAuthToken();

        const examResponse = await axios.get(`${API_BASE_URL}/exams/${routeExamId}`, {
          headers: { Authorization: `Bearer ${token}`, ...getOrganizationHeaders() }
        });

        if (isCancelled) {
          return;
        }

        await openQuestionBuilder(examResponse.data);
      } catch (err) {
        if (isCancelled) {
          return;
        }

        console.error('Failed to load exam builder page:', err);
        setError('Failed to load exam question builder');
      }
    };

    loadBuilderExam();

    return () => {
      isCancelled = true;
    };
  }, [API_BASE_URL, getAuthToken, openQuestionBuilder, routeExamId, selectedExamForQuestions?.id]);

  const closeQuestionBuilder = () => {
    setSelectedExamForQuestions(null);
    setQuestionBank([]);
    setExamQuestions([]);
    setCategoryNameMap({});
    setCategoryReleaseVersionMap({});
    setCourseCategoryIds({});
    setBuilderMessage(null);
    setSearchText('');
    setCategoryFilter(null);
    setDifficultyFilter(null);
    setCourseFilter(null);
    setReleaseVersionFilter(null);
    setQuestionBankPage(1);

    if (returnToPath) {
      navigate(returnToPath, { replace: true });
      return;
    }

    if (isQuestionBuilderRoute) {
      navigate('/manage-exams');
    }
  };

  const getSelectedQuestionIds = () => new Set(examQuestions.map((item) => item.questionId));

  const addQuestionToExam = (question: QuestionBankItem) => {
    const alreadySelected = examQuestions.some((item) => item.questionId === question.id);
    if (alreadySelected) {
      return;
    }

    setExamQuestions((prev) => [
      ...prev,
      {
        id: 0,
        examId: selectedExamForQuestions?.id || 0,
        questionId: question.id,
        questionText: question.questionText,
        categoryId: question.categoryId,
        difficultyLevel: question.difficultyLevel,
        isEnabled: true,
        orderIndex: prev.length,
        marks: 1,
        negativeMarks: 0
      }
    ]);
  };

  const removeQuestionFromExam = (questionId: number) => {
    setExamQuestions((prev) => prev.filter((item) => item.questionId !== questionId));
  };

  const updateExamQuestion = (questionId: number, patch: Partial<ExamQuestionItem>) => {
    setExamQuestions((prev) =>
      prev.map((item) =>
        item.questionId === questionId
          ? {
              ...item,
              ...patch
            }
          : item
      )
    );
  };

  const reorderExamQuestions = (sourceQuestionId: number, targetQuestionId: number) => {
    if (sourceQuestionId === targetQuestionId) return;

    setExamQuestions((prev) => {
      const sorted = [...prev].sort(
        (a, b) => (a.orderIndex ?? Number.MAX_SAFE_INTEGER) - (b.orderIndex ?? Number.MAX_SAFE_INTEGER)
      );

      const sourceIndex = sorted.findIndex((item) => item.questionId === sourceQuestionId);
      const targetIndex = sorted.findIndex((item) => item.questionId === targetQuestionId);

      if (sourceIndex < 0 || targetIndex < 0) {
        return prev;
      }

      const [movedItem] = sorted.splice(sourceIndex, 1);
      sorted.splice(targetIndex, 0, movedItem);

      return sorted.map((item, index) => ({
        ...item,
        orderIndex: index
      }));
    });
  };

  const handleSaveExamQuestions = async () => {
    if (!selectedExamForQuestions) return;

    try {
      setSavingBuilder(true);
      setBuilderMessage(null);
      setError(null);

      const tokenResponse = await getAccessTokenSilently().catch(() =>
        getAccessTokenSilently({ cacheMode: 'off' } as any)
      );

      const token =
        typeof tokenResponse === 'string'
          ? tokenResponse
          : (tokenResponse as { access_token?: string })?.access_token;

      if (!token) {
        throw new Error('Unable to get access token. Please sign in again and retry.');
      }

      const sortedQuestions = [...examQuestions].sort(
        (a, b) => (a.orderIndex ?? Number.MAX_SAFE_INTEGER) - (b.orderIndex ?? Number.MAX_SAFE_INTEGER)
      );

      const payload = {
        questions: sortedQuestions.map((item, index) => ({
          questionId: item.questionId,
          isEnabled: item.isEnabled,
          orderIndex: item.orderIndex ?? index,
          marks: item.marks > 0 ? item.marks : 1,
          negativeMarks: item.negativeMarks >= 0 ? item.negativeMarks : 0
        }))
      };

      const response = await axios.put(
        `${API_BASE_URL}/exams/${selectedExamForQuestions.id}/questions`,
        payload,
        { headers: { Authorization: `Bearer ${token}`, ...getOrganizationHeaders() } }
      );

      setExamQuestions((response.data || []).map((item: ExamQuestionItem, index: number) => ({
        ...item,
        orderIndex: item.orderIndex ?? index,
        marks: item.marks > 0 ? item.marks : 1,
        negativeMarks: item.negativeMarks >= 0 ? item.negativeMarks : 0
      })));
      setBuilderMessage('Exam questions saved successfully');
    } catch (err: any) {
      console.error('Failed to save exam questions:', err);
      if (!err?.response) {
        setError(err?.message || 'Failed to save exam questions');
      } else {
        setError(err.response?.data?.message || 'Failed to save exam questions');
      }
    } finally {
      setSavingBuilder(false);
    }
  };

  const selectedQuestionIds = getSelectedQuestionIds();

  const questionBankById = questionBank.reduce<Record<number, QuestionBankItem>>((map, question) => {
    map[question.id] = question;
    return map;
  }, {});

  const filteredQuestionBank = questionBank.filter((question) => {
    const search = searchText.trim().toLowerCase();
    const hasCorrectOption = !!question.correctOption && question.correctOption.trim().length > 0;
    const matchesSearch = !search || question.questionText.toLowerCase().includes(search);
    const matchesCategory = !categoryFilter || question.categoryId === categoryFilter;
    const matchesDifficulty = !difficultyFilter || question.difficultyLevel === difficultyFilter;
    const matchesCourse = !courseFilter || (courseCategoryIds[courseFilter] || []).includes(question.categoryId);
    const matchesReleaseVersion =
      !releaseVersionFilter ||
      categoryReleaseVersionMap[question.categoryId] === releaseVersionFilter;

    return hasCorrectOption && matchesSearch && matchesCategory && matchesDifficulty && matchesCourse && matchesReleaseVersion;
  });

  useEffect(() => {
    setQuestionBankPage(1);
  }, [searchText, categoryFilter, difficultyFilter, courseFilter, releaseVersionFilter, selectedExamForQuestions?.id]);

  const visibleQuestionBank = filteredQuestionBank.slice(0, questionBankPage * QUESTION_BANK_PAGE_SIZE);
  const hasMoreQuestionBank = visibleQuestionBank.length < filteredQuestionBank.length;

  const sortedSelectedQuestions = [...examQuestions].sort(
    (a, b) => (a.orderIndex ?? Number.MAX_SAFE_INTEGER) - (b.orderIndex ?? Number.MAX_SAFE_INTEGER)
  );

  const enabledCount = examQuestions.filter((item) => item.isEnabled).length;
  const totalMarks = examQuestions
    .filter((item) => item.isEnabled)
    .reduce((sum, item) => sum + (item.marks > 0 ? item.marks : 0), 0);

  const resetForm = () => {
    setFormData({
      type: 'Exam',
      name: '',
      description: '',
      timeLimitMinutes: 60,
      isActive: true,
      courseIds: []
    });
    setIsAdding(false);
    setEditingId(null);
  };

  const handleAdd = () => {
    setIsAdding(true);
    setEditingId(null);
    setFormData({
      type: 'Exam',
      name: '',
      description: '',
      timeLimitMinutes: 60,
      isActive: true,
      courseIds: []
    });
  };

  const handleEdit = (exam: Exam) => {
    const safeCourses = Array.isArray(exam?.courses) ? exam.courses : [];

    setIsAdding(false);
    setEditingId(exam.id);
    setFormData({
      type: exam.type || 'Exam',
      name: exam.name,
      description: exam.description || '',
      timeLimitMinutes: exam.timeLimitMinutes || 60,
      isActive: exam.isActive,
      courseIds: safeCourses.map(course => course.id)
    });
  };

  const handleCourseToggle = (courseId: number) => {
    setFormData(prev => ({
      ...prev,
      courseIds: prev.courseIds.includes(courseId)
        ? prev.courseIds.filter(id => id !== courseId)
        : [...prev.courseIds, courseId]
    }));
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      setError('Exam name is required');
      return;
    }

    try {
      const token = await getAuthToken();
      const payload = {
        type: formData.type,
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        timeLimitMinutes: formData.timeLimitMinutes > 0 ? formData.timeLimitMinutes : 60,
        isActive: formData.isActive,
        courseIds: formData.courseIds
      };

      if (isAdding) {
        await axios.post(`${API_BASE_URL}/exams`, payload, {
          headers: { Authorization: `Bearer ${token}`, ...getOrganizationHeaders() }
        });
      } else if (editingId) {
        await axios.put(`${API_BASE_URL}/exams/${editingId}`, payload, {
          headers: { Authorization: `Bearer ${token}`, ...getOrganizationHeaders() }
        });
      }

      if (selectedCourseId) {
        await fetchExamsForCourse(selectedCourseId);
      } else {
        await fetchExamsForCourse(null);
      }
      resetForm();
    } catch (err: any) {
      console.error('Failed to save exam:', err);
      setError(err.response?.data?.message || 'Failed to save exam');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this exam?')) return;

    try {
      const token = await getAuthToken();
      await axios.delete(`${API_BASE_URL}/exams/${id}`, {
        headers: { Authorization: `Bearer ${token}`, ...getOrganizationHeaders() }
      });
      if (selectedCourseId) {
        await fetchExamsForCourse(selectedCourseId);
      } else {
        await fetchExamsForCourse(null);
      }
    } catch (err: any) {
      console.error('Failed to delete exam:', err);
      setError(err.response?.data?.message || 'Failed to delete exam');
    }
  };

  const toggleExamStatus = async (exam: Exam) => {
    try {
      const token = await getAuthToken();
      await axios.put(
        `${API_BASE_URL}/exams/${exam.id}`,
        { isActive: !exam.isActive },
        { headers: { Authorization: `Bearer ${token}`, ...getOrganizationHeaders() } }
      );

      if (selectedCourseId) {
        await fetchExamsForCourse(selectedCourseId);
      } else {
        await fetchExamsForCourse(null);
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to update exam status');
    }
  };

  const examRows = useMemo<ExamRow[]>(
    () =>
      exams.map((exam) => ({
        id: exam.id,
        name: exam.name,
        type: exam.type || 'Exam',
        status: exam.isActive ? 'Active' : 'Inactive',
        timeLimit: exam.timeLimitMinutes,
        courses: exam.courses?.length ?? 0,
        exam,
      })),
    [exams]
  );

  const selectedExam = useMemo(
    () => exams.find((exam) => exam.id === selectedExamId) ?? null,
    [exams, selectedExamId]
  );

  useEffect(() => {
    if (!exams.length) {
      setSelectedExamId(null);
      return;
    }

    if (selectedExamId != null && !exams.some((exam) => exam.id === selectedExamId)) {
      setSelectedExamId(null);
    }
  }, [exams, selectedExamId]);

  const filteredExamRows = useMemo(() => {
    const search = examSearch.trim().toLowerCase();

    return examRows.filter((row) => {
      if (search) {
        const haystack = [
          row.name,
          row.type,
          row.status,
          row.timeLimit,
          row.exam.description ?? '',
          (row.exam.courses || []).map((course) => course.name).join(' '),
        ]
          .join(' ')
          .toLowerCase();

        if (!haystack.includes(search)) {
          return false;
        }
      }

      if (examStatusFilter !== 'all') {
        const isActive = examStatusFilter === 'active';
        if (row.exam.isActive !== isActive) {
          return false;
        }
      }

      if (examTypeFilter !== 'all' && row.type.toLowerCase() !== examTypeFilter) {
        return false;
      }

      return true;
    });
  }, [examRows, examSearch, examStatusFilter, examTypeFilter]);

  const sortedExamRows = useMemo(() => {
    const items = [...filteredExamRows];
    const direction = examSortDirection === 'asc' ? 1 : -1;

    items.sort((left, right) => {
      const leftValue = String(left[examSortKey]).toLowerCase();
      const rightValue = String(right[examSortKey]).toLowerCase();

      if (leftValue < rightValue) {
        return -1 * direction;
      }
      if (leftValue > rightValue) {
        return 1 * direction;
      }
      return 0;
    });

    return items;
  }, [filteredExamRows, examSortDirection, examSortKey]);

  const examTotalPages = Math.max(1, Math.ceil(sortedExamRows.length / EXAMS_PAGE_SIZE));

  useEffect(() => {
    if (examPage > examTotalPages) {
      setExamPage(examTotalPages);
    }
  }, [examPage, examTotalPages]);

  const pagedExamRows = useMemo(() => {
    const start = (examPage - 1) * EXAMS_PAGE_SIZE;
    return sortedExamRows.slice(start, start + EXAMS_PAGE_SIZE);
  }, [examPage, sortedExamRows]);

  const examColumns = useMemo<DataGridColumn<ExamRow>[]>(
    () => [
      {
        key: 'name',
        label: 'Definition',
        sortable: true,
        width: '42%',
        render: (row) => (
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 700, color: '#142541', overflowWrap: 'anywhere' }}>{row.name}</div>
            <div style={{ fontSize: '0.75rem', color: '#607399', marginTop: 2 }}>
              {row.exam.description || 'No description'}
            </div>
          </div>
        ),
      },
      {
        key: 'type',
        label: 'Type',
        sortable: true,
        width: '14%',
        render: (row) => row.type,
      },
      {
        key: 'status',
        label: 'Status',
        sortable: true,
        width: '14%',
        render: (row) => (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              borderRadius: 999,
              border: '1px solid #d1ddf3',
              color: '#3a5686',
              background: '#f4f8ff',
              padding: '2px 8px',
              fontSize: '0.73rem',
              fontWeight: 700,
            }}
          >
            {row.status}
          </span>
        ),
      },
      {
        key: 'timeLimit',
        label: 'Time (min)',
        sortable: true,
        width: '12%',
        render: (row) => row.timeLimit,
      },
      {
        key: 'courses',
        label: 'Courses',
        sortable: true,
        width: '10%',
        render: (row) => row.courses,
      },
    ],
    []
  );

  const examRowActions: DataGridRowAction<ExamRow>[] = [
    {
      key: 'edit',
      label: 'Edit',
      icon: <IconEdit size={14} />,
      onClick: (row) => {
        setSelectedExamId(row.exam.id);
        handleEdit(row.exam);
      },
    },
    {
      key: 'questions',
      label: 'Questions',
      icon: <IconRelationships size={14} />,
      onClick: (row) => navigate(`/manage-exams/${row.exam.id}/questions`),
    },
    {
      key: 'toggle',
      label: 'Toggle Active',
      icon: <IconCheckCircle size={14} />,
      onClick: (row) => {
        void toggleExamStatus(row.exam);
      },
    },
    {
      key: 'delete',
      label: 'Delete',
      icon: <IconDelete size={14} />,
      tone: 'destructive',
      onClick: (row) => {
        void handleDelete(row.exam.id);
      },
    },
  ];

  const examFilters = useMemo<FilterConfig[]>(
    () => [
      {
        key: 'status',
        label: 'Status',
        value: examStatusFilter,
        onChange: (value) => {
          setExamStatusFilter(value);
          setExamPage(1);
        },
        options: [
          { value: 'all', label: 'All statuses' },
          { value: 'active', label: 'Active' },
          { value: 'inactive', label: 'Inactive' },
        ],
      },
      {
        key: 'type',
        label: 'Type',
        value: examTypeFilter,
        onChange: (value) => {
          setExamTypeFilter(value);
          setExamPage(1);
        },
        options: [
          { value: 'all', label: 'All types' },
          { value: 'exam', label: 'Exam' },
          { value: 'homework', label: 'Homework' },
        ],
      },
      {
        key: 'course',
        label: 'Course',
        value: selectedCourseId != null ? String(selectedCourseId) : 'all',
        onChange: (value) => {
          setSelectedCourseId(value === 'all' ? null : Number(value));
          setExamPage(1);
        },
        options: [
          { value: 'all', label: 'All courses' },
          ...courses.map((c) => ({ value: String(c.id), label: c.name })),
        ],
      },
    ],
    [courses, examStatusFilter, examTypeFilter, selectedCourseId]
  );

  const handleExamSort = (columnKey: string) => {
    if (!['name', 'type', 'status', 'timeLimit', 'courses'].includes(columnKey)) {
      return;
    }

    const nextKey = columnKey as SortKey;
    if (examSortKey === nextKey) {
      setExamSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }

    setExamSortKey(nextKey);
    setExamSortDirection('asc');
  };

  if (loading) {
    return <div style={{ padding: 24, fontSize: 18 }}>Loading exams...</div>;
  }

  if (!isQuestionBuilderRoute) {
    const activeCount = exams.filter((exam) => exam.isActive).length;
    const inactiveCount = exams.length - activeCount;
    const questionBuilderReadyCount = exams.filter((exam) => (exam.courses?.length ?? 0) > 0).length;

    const headerActions: HeaderAction[] = [
      {
        key: 'create',
        label: 'Create Exam',
        variant: 'primary',
        onClick: handleAdd,
      },
      {
        key: 'refresh',
        label: 'Refresh',
        variant: 'secondary',
        onClick: () => {
          void fetchExamsForCourse(selectedCourseId);
        },
      },
    ];

    return (
      <div style={{ width: '100%', minHeight: 'calc(100vh - 260px)', display: 'grid', gap: 12 }}>
        <AdminBreadcrumbs
          items={[
            { label: 'Administration', to: '/analytics' },
            { label: 'Exams', isCurrent: true },
          ]}
        />

        <AdminPageHeader
          title="Manage Exams"
          subtitle={`${exams.length} definitions • ${activeCount} active • ${inactiveCount} inactive`}
          actions={headerActions}
        />

        {error && (
          <div
            style={{
              borderRadius: 10,
              border: '1px solid #fecaca',
              background: '#fff1f2',
              color: '#b91c1c',
              padding: '10px 12px',
              fontSize: '0.84rem',
            }}
          >
            {error}
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 10 }}>
          <AdminStatCard label="Total" value={exams.length} icon={<IconBook size={15} />} />
          <AdminStatCard label="Active" value={activeCount} icon={<IconCheckCircle size={15} />} accent="active" />
          <AdminStatCard label="Inactive" value={inactiveCount} icon={<IconMetadata size={15} />} accent="draft" />
          <AdminStatCard label="With Courses" value={questionBuilderReadyCount} icon={<IconRelationships size={15} />} />
        </div>

        <AdminFilterBar
          searchValue={examSearch}
          onSearchChange={(value) => {
            setExamSearch(value);
            setExamPage(1);
          }}
          searchPlaceholder="Search definitions by name, type, description, status..."
          filters={examFilters}
        />

        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(440px, 58%) minmax(340px, 42%)', gap: 12, minHeight: 580 }}>
          <div style={{ minHeight: 0 }}>
            <AdminDataGrid<ExamRow>
              rows={pagedExamRows}
              columns={examColumns}
              selectedRowId={selectedExamId}
              onRowClick={(row) => handleEdit(row.exam)}
              selectedIds={selectedIds}
              onSelectedIdsChange={setSelectedIds}
              sortKey={examSortKey}
              sortDirection={examSortDirection}
              onSort={handleExamSort}
              page={examPage}
              pageSize={EXAMS_PAGE_SIZE}
              totalCount={sortedExamRows.length}
              onPageChange={setExamPage}
              emptyMessage="No exams found."
              rowActions={examRowActions}
              alwaysShowRowActions
            />
          </div>

          <div style={{ border: '1px solid #dfe8f8', borderRadius: 12, background: '#fff', boxShadow: '0 8px 18px rgba(18, 42, 80, 0.08)', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <div style={{ padding: '14px 14px 10px', borderBottom: '1px solid #edf2fb' }}>
              <h3 style={{ margin: 0, width: '100%', boxSizing: 'border-box', fontSize: '1.02rem', fontWeight: 800, color: '#173159', background: 'linear-gradient(180deg, #f5f9ff 0%, #ebf3ff 100%)', border: '1px solid #cfe0fb', borderRadius: 10, padding: '9px 11px' }}>
                {isAdding ? 'Create Exam' : editingId ? 'Edit Exam' : selectedExam ? selectedExam.name : 'Select an Exam'}
              </h3>
            </div>

            <div style={{ padding: 14, overflowY: 'auto', minHeight: 0, display: 'grid', gap: 14 }}>
              {!selectedExam && !isAdding && !editingId && (
                <div
                  style={{
                    minHeight: 260,
                    border: '1px dashed #c9d6ef',
                    borderRadius: 12,
                    background: '#f8fbff',
                    display: 'grid',
                    placeContent: 'center',
                    textAlign: 'center',
                    color: '#4f658f',
                    gap: 8,
                    padding: 16,
                  }}
                >
                  <div style={{ color: '#35568a', display: 'inline-flex', justifySelf: 'center' }}><IconBook size={26} /></div>
                  <div style={{ fontWeight: 700 }}>Select an exam from the list</div>
                  <div style={{ fontSize: '0.82rem' }}>or</div>
                  <button type="button" className="amd-btn amd-btn-primary" onClick={handleAdd}>Create a new exam</button>
                </div>
              )}

              {selectedExam && !isAdding && !editingId && (
                <section style={{ display: 'grid', gap: 8, paddingBottom: 10, borderBottom: '1px solid #edf2fb' }}>
                  <AdminSectionHeading icon={<IconGeneral size={14} />} title="Selected Exam" />
                  <div style={{ fontSize: '0.83rem', color: '#49638d' }}>Type: {selectedExam.type || 'Exam'}</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    <button type="button" className="amd-btn amd-btn-secondary" onClick={() => navigate(`/manage-exams/${selectedExam.id}/questions`)}>Manage Questions</button>
                  </div>
                </section>
              )}

              {(isAdding || editingId) && (
              <section style={{ display: 'grid', gap: 10 }}>
                <AdminSectionHeading icon={<IconMetadata size={14} />} title="Exam Form" />
                <select
                  value={formData.type}
                  onChange={(e) => setFormData((prev) => ({ ...prev, type: e.target.value }))}
                >
                  <option value="Exam">Exam</option>
                  <option value="Homework">Homework</option>
                </select>
                <input
                  type="text"
                  placeholder="Exam name"
                  value={formData.name}
                  onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                />
                <textarea
                  placeholder="Description"
                  value={formData.description}
                  onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                  rows={4}
                />
                <input
                  type="number"
                  min={1}
                  placeholder="Time limit in minutes"
                  value={formData.timeLimitMinutes}
                  onChange={(e) => setFormData((prev) => ({ ...prev, timeLimitMinutes: Number(e.target.value) }))}
                />
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#35507a', fontSize: '0.84rem' }}>
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData((prev) => ({ ...prev, isActive: e.target.checked }))}
                  />
                  Active
                </label>
                <div style={{ border: '1px solid #e0e8f7', borderRadius: 8, padding: 10, maxHeight: 180, overflowY: 'auto', display: 'grid', gap: 6 }}>
                  {courses.map((course) => (
                    <label key={course.id} style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#35507a', fontSize: '0.84rem' }}>
                      <input
                        type="checkbox"
                        checked={formData.courseIds.includes(course.id)}
                        onChange={() => handleCourseToggle(course.id)}
                      />
                      {course.name}
                    </label>
                  ))}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  <button type="button" className="amd-btn amd-btn-primary" onClick={() => { void handleSave(); }}>
                    {isAdding ? 'Create Exam' : editingId ? 'Save Changes' : 'Save'}
                  </button>
                  {editingId && (
                    <button
                      type="button"
                      className="amd-btn amd-btn-secondary"
                      onClick={() => navigate(`/manage-exams/${editingId}/questions`)}
                    >
                      Manage Questions
                    </button>
                  )}
                  <button type="button" className="amd-btn amd-btn-ghost" onClick={resetForm}>Cancel</button>
                  {selectedExam && !isAdding && !editingId && (
                    <button type="button" className="amd-btn amd-btn-ghost" onClick={() => { void handleDelete(selectedExam.id); }}>
                      Delete Selected
                    </button>
                  )}
                </div>
              </section>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const builderHeaderActions: HeaderAction[] = [
    {
      key: 'back',
      label: 'Back to Exams',
      variant: 'secondary',
      onClick: closeQuestionBuilder,
    },
  ];

  return (
    <MathJaxContext>
    <div style={{ width: '100%', minHeight: 'calc(100vh - 260px)', display: 'grid', gap: 12 }}>
      <AdminBreadcrumbs
        items={[
          { label: 'Administration', to: '/analytics' },
          { label: 'Exams', to: '/manage-exams' },
          { label: 'Question Builder', isCurrent: true },
        ]}
      />

      <AdminPageHeader
        title="Manage Exam Questions"
        subtitle={selectedExamForQuestions ? `${selectedExamForQuestions.type || 'Exam'}: ${selectedExamForQuestions.name}` : 'Question builder'}
        actions={builderHeaderActions}
      />

      {error && (
        <div style={{ marginBottom: 16, backgroundColor: '#ffebee', color: '#c62828', padding: 12, borderRadius: 6 }}>
          {error}
          <button
            onClick={() => setError(null)}
            style={{ float: 'right', border: 'none', background: 'transparent', cursor: 'pointer', color: '#c62828' }}
          >
            ×
          </button>
        </div>
      )}

      {!isQuestionBuilderRoute && (isAdding || editingId) && (
        <div style={{ backgroundColor: '#f8f8f8', border: '1px solid #ddd', borderRadius: 8, padding: 16, marginBottom: 20 }}>
          <h3 style={{ marginTop: 0 }}>{isAdding ? 'Create Exam' : 'Edit Exam'}</h3>

          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Exam Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              style={{ width: '100%', padding: 10, borderRadius: 4, border: '1px solid #ccc' }}
            />
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              style={{ width: '100%', minHeight: 90, padding: 10, borderRadius: 4, border: '1px solid #ccc', resize: 'vertical' }}
            />
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Time Limit (minutes)</label>
            <input
              type="number"
              min={1}
              value={formData.timeLimitMinutes}
              onChange={(e) => setFormData(prev => ({ ...prev, timeLimitMinutes: Number(e.target.value) }))}
              style={{ width: '100%', padding: 10, borderRadius: 4, border: '1px solid #ccc' }}
            />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600 }}>
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
              />
              Active
            </label>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>Related Courses</label>
            <div style={{ maxHeight: 220, overflowY: 'auto', border: '1px solid #ddd', borderRadius: 6, padding: 10, backgroundColor: '#fff' }}>
              {courses.length === 0 ? (
                <div style={{ color: '#666' }}>No courses found.</div>
              ) : (
                courses.map(course => (
                  <label key={course.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={formData.courseIds.includes(course.id)}
                      onChange={() => handleCourseToggle(course.id)}
                    />
                    <span>{course.name}</span>
                  </label>
                ))
              )}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10 }}>
            <button
              onClick={handleSave}
              style={{ backgroundColor: '#2196f3', color: '#fff', border: 'none', borderRadius: 6, padding: '10px 16px', cursor: 'pointer', fontWeight: 600 }}
            >
              Save
            </button>
            <button
              onClick={resetForm}
              style={{ backgroundColor: '#757575', color: '#fff', border: 'none', borderRadius: 6, padding: '10px 16px', cursor: 'pointer', fontWeight: 600 }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {selectedExamForQuestions && (
        <div style={{ border: '1px solid #ddd', borderRadius: 8, backgroundColor: '#fff', padding: 16, marginBottom: 20, minHeight: BUILDER_SECTION_MIN_HEIGHT }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 12, flexWrap: 'wrap' }}>
            <div>
              <h3 style={{ margin: 0, color: '#222' }}>Exam Builder: {selectedExamForQuestions.name}</h3>
              <p style={{ margin: '6px 0 0 0', color: '#666' }}>
                Select questions from the bank and configure enabled status, order, and marks.
              </p>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={handleSaveExamQuestions}
                disabled={savingBuilder || loadingBuilder}
                style={{
                  backgroundColor: savingBuilder || loadingBuilder ? '#90caf9' : '#1976d2',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 6,
                  padding: '10px 14px',
                  cursor: savingBuilder || loadingBuilder ? 'not-allowed' : 'pointer',
                  fontWeight: 600
                }}
              >
                {savingBuilder ? 'Saving...' : 'Save Questions'}
              </button>
              <button
                onClick={closeQuestionBuilder}
                disabled={savingBuilder}
                style={{
                  backgroundColor: '#757575',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 6,
                  padding: '10px 14px',
                  cursor: savingBuilder ? 'not-allowed' : 'pointer',
                  fontWeight: 600
                }}
              >
                Close Builder
              </button>
            </div>
          </div>

          {builderMessage && (
            <div style={{ marginBottom: 12, backgroundColor: '#e8f5e9', color: '#2e7d32', padding: 10, borderRadius: 6 }}>
              {builderMessage}
            </div>
          )}

          {loadingBuilder ? (
            <div style={{ minHeight: BUILDER_SECTION_MIN_HEIGHT - 160, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#666', backgroundColor: '#fafafa', border: '1px solid #eee', borderRadius: 8, padding: 16 }}>
              Loading question builder...
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.3fr) minmax(320px, 0.7fr)', gap: 16, alignItems: 'start' }}>
              <div style={{ border: '1px solid #eee', borderRadius: 8, padding: 12, backgroundColor: '#fafafa', textAlign: 'left', minWidth: 0 }}>
                <h4 style={{ marginTop: 0, marginBottom: 12, color: '#333' }}>Question Bank</h4>

                <div style={{ display: 'grid', gap: 8, marginBottom: 12, minWidth: 0 }}>
                  <input
                    type="text"
                    value={searchText}
                    placeholder="Search by question text"
                    onChange={(e) => setSearchText(e.target.value)}
                    style={{ padding: 8, borderRadius: 4, border: '1px solid #ccc', width: '100%', minWidth: 0, boxSizing: 'border-box' }}
                  />

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8, minWidth: 0, width: '100%' }}>
                    <select
                      value={selectedCourseId ?? ''}
                      onChange={(e) => {
                        const nextCourseId = e.target.value ? Number(e.target.value) : null;
                        setSelectedCourseId(nextCourseId);
                        setCourseFilter(null);
                        setQuestionBank([]);
                        if (nextCourseId) {
                          void loadLeafCategoriesForCourse(nextCourseId);
                        }
                      }}
                      style={{ padding: 8, borderRadius: 4, border: '1px solid #ccc', width: '100%', minWidth: 0, boxSizing: 'border-box' }}
                      disabled={loadingLeafCategories}
                    >
                      <option value="">Select Course</option>
                      {courseOptions.map((course) => (
                        <option key={course.id} value={course.id}>{course.pathLabel || course.name}</option>
                      ))}
                    </select>

                    <select
                      value={categoryFilter || ''}
                      onChange={(e) => setCategoryFilter(e.target.value ? Number(e.target.value) : null)}
                      style={{ padding: 8, borderRadius: 4, border: '1px solid #ccc', width: '100%', minWidth: 0, boxSizing: 'border-box' }}
                      disabled={loadingLeafCategories || !selectedCourseId}
                    >
                      <option value="">Select Category</option>
                      {courseCategoryOptions.map((category) => (
                        <option key={category.id} value={category.id}>{category.pathLabel || category.label || category.name}</option>
                      ))}
                    </select>

                    <select
                      value={difficultyFilter || ''}
                      onChange={(e) => setDifficultyFilter(e.target.value ? Number(e.target.value) : null)}
                      style={{ padding: 8, borderRadius: 4, border: '1px solid #ccc', width: '100%', minWidth: 0, boxSizing: 'border-box' }}
                    >
                      <option value="">All Difficulties</option>
                      <option value="1">Easy</option>
                      <option value="2">Medium</option>
                      <option value="3">Hard</option>
                    </select>

                    <select
                      value={releaseVersionFilter || ''}
                      onChange={(e) => setReleaseVersionFilter(e.target.value ? Number(e.target.value) : null)}
                      style={{ padding: 8, borderRadius: 4, border: '1px solid #ccc', width: '100%', minWidth: 0, boxSizing: 'border-box' }}
                    >
                      <option value="">All Versions</option>
                      <option value="1">Version 1</option>
                      <option value="2">Version 2</option>
                      <option value="3">Version 3</option>
                      <option value="4">Version 4</option>
                    </select>
                  </div>
                </div>

                <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>
                  {loadingQuestionBank
                    ? 'Loading questions for the selected category...'
                    : categoryFilter
                      ? `Showing ${visibleQuestionBank.length} of ${filteredQuestionBank.length} questions`
                      : 'Select a category to load questions'}
                </div>

                <div style={{ maxHeight: 'calc(100vh - 340px)', minHeight: 320, overflowY: 'auto', display: 'grid', gap: 8 }}>
                  {!categoryFilter ? (
                    <div style={{ color: '#777' }}>Choose a category to populate the question bank.</div>
                  ) : loadingQuestionBank ? (
                    <div style={{ color: '#777' }}>Loading questions...</div>
                  ) : filteredQuestionBank.length === 0 ? (
                    <div style={{ color: '#777' }}>No questions match the selected category.</div>
                  ) : (
                    visibleQuestionBank.map((question) => {
                      const selected = selectedQuestionIds.has(question.id);
                      const options = [
                        { label: 'A', value: question.optionA },
                        { label: 'B', value: question.optionB },
                        { label: 'C', value: question.optionC },
                        { label: 'D', value: question.optionD },
                        { label: 'E', value: question.optionE }
                      ].filter((option) => !!option.value && option.value.trim().length > 0);

                      return (
                        <div
                          key={question.id}
                          style={{
                            border: selected ? '1px solid #90caf9' : '1px solid #e0e0e0',
                            backgroundColor: selected ? '#e3f2fd' : '#fff',
                            borderRadius: 6,
                            padding: 10
                          }}
                        >
                          <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>
                            Q{question.id} • {categoryNameMap[question.categoryId] || `Category ${question.categoryId}`} • Difficulty {question.difficultyLevel}
                          </div>
                          <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>
                            Release Version: {categoryReleaseVersionMap[question.categoryId] ?? 'N/A'}
                          </div>
                          <div style={{ color: '#333', marginBottom: 8, lineHeight: 1.4, textAlign: 'left' }}>
                            <MathJax dynamic>
                              <div dangerouslySetInnerHTML={{ __html: toHtmlMarkup(question.questionText) }} />
                            </MathJax>
                          </div>

                          {options.length > 0 && (
                            <div style={{ marginBottom: 8, fontSize: 12, color: '#444', display: 'grid', gap: 3, textAlign: 'left' }}>
                              {options.map((option) => (
                                <div key={`${question.id}-${option.label}`} style={{ textAlign: 'left' }}>
                                  <strong>{option.label}.</strong>{' '}
                                  <span style={{ display: 'inline-block' }}>
                                    <MathJax inline dynamic>
                                      <span dangerouslySetInnerHTML={{ __html: toHtmlMarkup(option.value) }} />
                                    </MathJax>
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}

                          <div style={{ marginBottom: 8, fontSize: 12, color: '#1b5e20', fontWeight: 600 }}>
                            Correct Answer:{' '}
                            {hasMathSyntax(question.correctOption) ? (
                              <span style={{ display: 'inline-block' }}>
                                  <MathJax inline dynamic>{prepareForMathJax(question.correctOption)}</MathJax>
                              </span>
                            ) : (
                              <span>{question.correctOption}</span>
                            )}
                          </div>

                          <button
                            onClick={() => addQuestionToExam(question)}
                            disabled={selected}
                            style={{
                              backgroundColor: selected ? '#bdbdbd' : '#43a047',
                              color: '#fff',
                              border: 'none',
                              borderRadius: 4,
                              padding: '6px 10px',
                              cursor: selected ? 'not-allowed' : 'pointer',
                              fontWeight: 600,
                              fontSize: 12
                            }}
                          >
                            {selected ? 'Selected' : 'Add to Exam'}
                          </button>
                        </div>
                      );
                    })
                  )}

                  {hasMoreQuestionBank && (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '8px 0 4px 0' }}>
                      <button
                        onClick={() => setQuestionBankPage((prev) => prev + 1)}
                        style={{
                          backgroundColor: '#1976d2',
                          color: '#fff',
                          border: 'none',
                          borderRadius: 6,
                          padding: '8px 14px',
                          cursor: 'pointer',
                          fontWeight: 600,
                          fontSize: 12
                        }}
                      >
                        Load More ({Math.min(QUESTION_BANK_PAGE_SIZE, filteredQuestionBank.length - visibleQuestionBank.length)} more)
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div style={{ border: '1px solid #eee', borderRadius: 8, padding: 12, backgroundColor: '#fafafa', textAlign: 'left', minWidth: 0, maxWidth: 700 }}>
                <h4 style={{ marginTop: 0, marginBottom: 12, color: '#333' }}>Exam Questions</h4>

                <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>
                  Drag and drop cards to reorder questions.
                </div>

                <div
                  style={{
                    position: 'sticky',
                    top: 0,
                    backgroundColor: '#fff',
                    border: '1px solid #e0e0e0',
                    borderRadius: 6,
                    padding: 10,
                    marginBottom: 10,
                    display: 'flex',
                    gap: 12,
                    flexWrap: 'wrap'
                  }}
                >
                  <span><strong>Selected:</strong> {examQuestions.length}</span>
                  <span><strong>Enabled:</strong> {enabledCount}</span>
                  <span><strong>Total Marks:</strong> {totalMarks.toFixed(2)}</span>
                </div>

                <div style={{ maxHeight: 'calc(100vh - 340px)', minHeight: 320, overflowY: 'auto', display: 'grid', gap: 8 }}>
                  {sortedSelectedQuestions.length === 0 ? (
                    <div style={{ color: '#777' }}>No questions selected for this exam yet.</div>
                  ) : (
                    sortedSelectedQuestions.map((item) => (
                      (() => {
                        const questionDetails = questionBankById[item.questionId];
                        const options = [
                          { label: 'A', value: questionDetails?.optionA },
                          { label: 'B', value: questionDetails?.optionB },
                          { label: 'C', value: questionDetails?.optionC },
                          { label: 'D', value: questionDetails?.optionD },
                          { label: 'E', value: questionDetails?.optionE }
                        ].filter((option) => !!option.value && option.value.trim().length > 0);

                        return (
                      <div
                        key={item.questionId}
                        draggable
                        onDragStart={() => setDraggingQuestionId(item.questionId)}
                        onDragEnd={() => setDraggingQuestionId(null)}
                        onDragOver={(event) => event.preventDefault()}
                        onDrop={(event) => {
                          event.preventDefault();
                          if (draggingQuestionId) {
                            reorderExamQuestions(draggingQuestionId, item.questionId);
                          }
                          setDraggingQuestionId(null);
                        }}
                        style={{
                          border: draggingQuestionId === item.questionId ? '1px dashed #1976d2' : '1px solid #e0e0e0',
                          borderRadius: 6,
                          backgroundColor: draggingQuestionId === item.questionId ? '#f5f9ff' : '#fff',
                          padding: 10,
                          cursor: 'grab'
                        }}
                      >
                        <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>
                          Q{item.questionId} • {categoryNameMap[item.categoryId] || `Category ${item.categoryId}`} • Difficulty {item.difficultyLevel}
                        </div>
                        <div style={{ color: '#333', marginBottom: 10, lineHeight: 1.4, textAlign: 'left' }}>
                          <MathJax dynamic>
                            <div dangerouslySetInnerHTML={{ __html: toHtmlMarkup(item.questionText) }} />
                          </MathJax>
                        </div>

                        {options.length > 0 && (
                          <div style={{ marginBottom: 8, fontSize: 12, color: '#444', display: 'grid', gap: 3, textAlign: 'left' }}>
                            {options.map((option) => (
                              <div key={`selected-${item.questionId}-${option.label}`} style={{ textAlign: 'left' }}>
                                <strong>{option.label}.</strong>{' '}
                                <span style={{ display: 'inline-block' }}>
                                  <MathJax inline dynamic>
                                    <span dangerouslySetInnerHTML={{ __html: toHtmlMarkup(option.value) }} />
                                  </MathJax>
                                </span>
                              </div>
                            ))}
                          </div>
                        )}

                        {questionDetails?.correctOption && (
                          <div style={{ marginBottom: 10, fontSize: 12, color: '#1b5e20', fontWeight: 600 }}>
                            Correct Answer:{' '}
                            {hasMathSyntax(questionDetails.correctOption) ? (
                              <span style={{ display: 'inline-block' }}>
                                  <MathJax inline dynamic>{prepareForMathJax(questionDetails.correctOption)}</MathJax>
                              </span>
                            ) : (
                              <span>{questionDetails.correctOption}</span>
                            )}
                          </div>
                        )}

                        <div style={{ display: 'grid', gridTemplateColumns: 'auto minmax(45px, 0.8fr) minmax(45px, 0.8fr) minmax(45px, 0.8fr) auto', gap: 8, alignItems: 'center', minWidth: 0 }}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#333', minWidth: 0 }}>
                            <input
                              type="checkbox"
                              checked={item.isEnabled}
                              onChange={(e) => updateExamQuestion(item.questionId, { isEnabled: e.target.checked })}
                            />
                            Enabled
                          </label>

                          <input
                            type="number"
                            min={0}
                            value={item.orderIndex ?? 0}
                            onChange={(e) => updateExamQuestion(item.questionId, { orderIndex: Number(e.target.value) })}
                            style={{ padding: 8, borderRadius: 4, border: '1px solid #ccc' }}
                            title="Order"
                          />

                          <input
                            type="number"
                            min={0.25}
                            step={0.25}
                            value={item.marks}
                            onChange={(e) => updateExamQuestion(item.questionId, { marks: Number(e.target.value) })}
                            style={{ padding: 8, borderRadius: 4, border: '1px solid #ccc' }}
                            title="Marks"
                          />

                          <input
                            type="number"
                            min={0}
                            step={0.25}
                            value={item.negativeMarks}
                            onChange={(e) => updateExamQuestion(item.questionId, { negativeMarks: Number(e.target.value) })}
                            style={{ padding: 8, borderRadius: 4, border: '1px solid #ccc' }}
                            title="Negative Marks"
                          />

                          <button
                            onClick={() => removeQuestionFromExam(item.questionId)}
                            style={{
                              backgroundColor: '#e53935',
                              color: '#fff',
                              border: 'none',
                              borderRadius: 4,
                              padding: '8px 10px',
                              cursor: 'pointer',
                              fontWeight: 600,
                              fontSize: 12
                            }}
                          >
                            Remove
                          </button>
                        </div>

                        <div style={{ marginTop: 6, fontSize: 11, color: '#777' }}>
                          Order • Marks • Negative Marks
                        </div>
                      </div>
                        );
                      })()
                    ))
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {!isQuestionBuilderRoute && <div style={{ display: 'grid', gap: 12 }}>
        {exams.length === 0 ? (
          <div style={{ padding: 16, border: '1px solid #ddd', borderRadius: 8, color: '#666' }}>No exams created yet.</div>
        ) : (
          exams.map(exam => (
            <div key={exam.id} style={{ border: '1px solid #ddd', borderRadius: 8, padding: 16, backgroundColor: '#fff' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
                <div>
                  <h3 style={{ marginTop: 0, marginBottom: 8, color: '#222' }}>{exam.name}</h3>
                  {exam.description && <p style={{ marginTop: 0, color: '#444', lineHeight: 1.5 }}>{exam.description}</p>}
                  <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 600, color: '#555' }}>Courses:</span>
                    {(exam.courses?.length ?? 0) > 0 ? (
                      (exam.courses || []).map(course => (
                        <span
                          key={course.id}
                          style={{ backgroundColor: '#e3f2fd', color: '#1565c0', borderRadius: 999, padding: '3px 10px', fontSize: 12, fontWeight: 600 }}
                        >
                          {course.name}
                        </span>
                      ))
                    ) : (
                      <span style={{ color: '#777' }}>No linked courses</span>
                    )}
                  </div>
                  <div style={{ marginTop: 10, fontSize: 12, color: '#666' }}>
                    Status: {exam.isActive ? 'Active' : 'Inactive'}
                  </div>
                  <div style={{ marginTop: 4, fontSize: 12, color: '#666' }}>
                    Time Limit: {exam.timeLimitMinutes} minutes
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 120 }}>
                  <button
                    onClick={() => handleEdit(exam)}
                    style={{ backgroundColor: '#ff9800', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 10px', cursor: 'pointer', fontWeight: 600 }}
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => navigate(`/manage-exams/${exam.id}/questions`)}
                    style={{ backgroundColor: '#2e7d32', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 10px', cursor: 'pointer', fontWeight: 600 }}
                  >
                    Manage Questions
                  </button>
                  <button
                    onClick={async () => {
                      try {
                        const token = await getAccessTokenSilently();
                        await axios.put(
                          `${API_BASE_URL}/exams/${exam.id}`,
                          { isActive: !exam.isActive },
                          { headers: { Authorization: `Bearer ${token}` } }
                        );
                        if (selectedCourseId) {
                          await fetchExamsForCourse(selectedCourseId);
                        } else {
                          await fetchExamsForCourse(null);
                        }
                      } catch (err: any) {
                        setError(err.response?.data?.message || 'Failed to update exam status');
                      }
                    }}
                    style={{ backgroundColor: '#607d8b', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 10px', cursor: 'pointer', fontWeight: 600 }}
                  >
                    {exam.isActive ? 'Deactivate' : 'Activate'}
                  </button>
                  <button
                    onClick={() => handleDelete(exam.id)}
                    style={{ backgroundColor: '#f44336', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 10px', cursor: 'pointer', fontWeight: 600 }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>}
    </div>
    </MathJaxContext>
  );
};

export default ManageExams;
